import React from 'react';

import { ActionIcon, Checkbox, Group, Table, Text, Tooltip } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSelector } from '@tabler/icons-react';

import { useFeatureFlag } from '@/store/slices/api/featureFlagsApiSlice';

import { ReportFilterMenu } from './ReportFilterMenu';
import styles from './TableHeader.module.scss';

export type SortDirection = 'ASC' | 'DESC' | null;

interface TableHeaderProps {
  selectedPreviousReportFilters?: string[];
  selectedCurrentReportFilters?: string[];
  isNonPractitionerSignedCurrentReport?: boolean;
  isPendingReviewSelectedCurrentReport?: boolean;
  isRecentSignedSelected?: boolean;
  isDownloadedSelected?: boolean;
  onPreviousReportFiltersChange?: (
    filters: string[],
    isRecentSigned?: boolean,
    isNotDownloaded?: boolean
  ) => void;
  onCurrentReportFiltersChange?: (
    filters: string[],
    isNonPractitionerSelected: boolean,
    isPendingReviewSelected: boolean
  ) => void;
  isLoading?: boolean;
  sortBy?: string;
  sortDirection?: SortDirection;
  onSort?: (field: string) => void;
  isAllSelected?: boolean;
  isIndeterminate?: boolean;
  onSelectAll?: (checked: boolean) => void;
  isSelectAllDisabled?: boolean;
}

export const SortIcon: React.FC<{
  direction: SortDirection;
  onClick: () => void;
  disabled?: boolean;
}> = ({ direction, onClick, disabled = false }) => {
  const getIcon = () => {
    if (direction === 'ASC') return <IconChevronUp size={14} />;
    if (direction === 'DESC') return <IconChevronDown size={14} />;
    return <IconSelector size={14} />;
  };

  return (
    <ActionIcon
      variant="subtle"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      style={{
        opacity: disabled ? 0.3 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}>
      {getIcon()}
    </ActionIcon>
  );
};

const TableHeader: React.FC<TableHeaderProps> = ({
  selectedPreviousReportFilters = ['GREEN', 'RED', 'YELLOW'],
  selectedCurrentReportFilters = ['GREEN', 'RED', 'YELLOW'],
  isNonPractitionerSignedCurrentReport = false,
  isPendingReviewSelectedCurrentReport = false,
  onPreviousReportFiltersChange,
  onCurrentReportFiltersChange,
  isRecentSignedSelected,
  isDownloadedSelected,
  isLoading = false,
  sortBy,
  sortDirection = null,
  onSort,
  isAllSelected,
  isIndeterminate,
  onSelectAll,
  isSelectAllDisabled = false
}) => {
  const isNewFiltersUXEnabled = useFeatureFlag('new_filters_dx');

  const handleSort = (field: string) => {
    if (onSort && !isLoading) {
      onSort(field);
    }
  };

  const getPatientSortDirection = (): SortDirection => {
    return sortBy === 'familyName' ? (sortDirection ?? null) : null;
  };

  const getClinicSortDirection = (): SortDirection => {
    return sortBy === 'name' ? (sortDirection ?? null) : null;
  };

  const getStatusSortDirection = (): SortDirection => {
    return sortBy === 'status' ? (sortDirection ?? null) : null;
  };

  const handlePreviousReportFiltersApply = (
    filters: string[],
    _: boolean,
    __: boolean,
    isRecentSigned?: boolean,
    isNotDownloaded?: boolean
  ) => {
    onPreviousReportFiltersChange?.(filters, isRecentSigned, isNotDownloaded);
  };

  const handleCurrentReportFiltersApply = (
    filters: string[],
    isNonPractitionerSelected: boolean,
    isPendingReviewSelected: boolean
  ) => {
    onCurrentReportFiltersChange?.(filters, isNonPractitionerSelected, isPendingReviewSelected);
  };

  return (
    <Table.Thead className={styles.header}>
      <Table.Tr>
        <Table.Th style={{ width: 40 }}>
          <Tooltip
            label="No reports to download on this page"
            disabled={!isSelectAllDisabled}
            withArrow>
            <div style={{ display: 'inline-block' }}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={(event) => onSelectAll?.(event.currentTarget.checked)}
                disabled={isSelectAllDisabled}
                styles={{ input: { cursor: isSelectAllDisabled ? 'not-allowed' : 'pointer' } }}
              />
            </div>
          </Tooltip>
        </Table.Th>
        <Table.Th style={{ width: 250 }}>
          <Text className={styles.tableHeading}>
            <Group gap="xs" wrap="nowrap" justify="space-between">
              Patient
              <SortIcon
                direction={getPatientSortDirection()}
                onClick={() => handleSort('familyName')}
                disabled={isLoading}
              />
            </Group>
          </Text>
        </Table.Th>
        <Table.Th style={{ width: 200 }}>
          <Text className={styles.tableHeading}>
            <Group gap="xs" wrap="nowrap" justify="space-between">
              Status
              <SortIcon
                direction={getStatusSortDirection()}
                onClick={() => handleSort('status')}
                disabled={isLoading}
              />
            </Group>
          </Text>
        </Table.Th>
        <Table.Th style={{ width: 200 }}>
          <Text className={styles.tableHeading}>
            <Group gap="xs" wrap="nowrap" justify="space-between">
              Clinic
              <SortIcon
                direction={getClinicSortDirection()}
                onClick={() => handleSort('name')}
                disabled={isLoading}
              />
            </Group>
          </Text>
        </Table.Th>
        <Table.Th style={{ width: 150 }}>
          <Text className={styles.tableHeading}>Last Transmission</Text>
        </Table.Th>
        <Table.Th style={{ width: 300 }}>
          <Text className={styles.tableHeading}>
            <Group gap="xs" wrap="nowrap" justify="space-between">
              Previous Report
              {!isLoading && !isNewFiltersUXEnabled && (
                <ReportFilterMenu
                  selectedFilters={selectedPreviousReportFilters}
                  showNoReportOption={false}
                  showPreviousReportOptions={true}
                  isRecentSignedSelected={isRecentSignedSelected}
                  isDownloadedSelected={isDownloadedSelected}
                  onApplyFilters={handlePreviousReportFiltersApply}
                />
              )}
            </Group>
          </Text>
        </Table.Th>
        <Table.Th style={{ width: 420 }}>
          <Text className={styles.tableHeading}>
            <Group gap="xs" wrap="nowrap" justify="space-between">
              Current Report
              {!isLoading && !isNewFiltersUXEnabled && (
                <ReportFilterMenu
                  selectedFilters={selectedCurrentReportFilters}
                  isNonPractitionerSignedCRSelected={isNonPractitionerSignedCurrentReport}
                  isPendingReviewSelectedCRSelected={isPendingReviewSelectedCurrentReport}
                  onApplyFilters={handleCurrentReportFiltersApply}
                />
              )}
            </Group>
          </Text>
        </Table.Th>
        <Table.Th style={{ width: 80 }}>
          <Text className={styles.tableHeading}></Text>
        </Table.Th>
      </Table.Tr>
    </Table.Thead>
  );
};

export default TableHeader;
